import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { logger } from "./lib/logger";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { AdminToastHost } from "./admin-panel/components/AdminToast";
import { AnalyticsProvider } from "./components/analytics/AnalyticsProvider";
import SignIn from "./pages/AuthPages/SignIn";
import SignUp from "./pages/AuthPages/SignUp";
import ForgotPassword from "./pages/AuthPages/ForgotPassword";
import ResetPassword from "./pages/AuthPages/ResetPassword";
import ResetPasswordRedirect from "./pages/AuthPages/ResetPasswordRedirect";
import SetupAccount from "./pages/AuthPages/SetupAccount";
import AccountReactivate from "./pages/AuthPages/AccountReactivate";
import NotFound from "./pages/OtherPage/NotFound";
const UserProfiles = lazyWithRetry(() => import("./pages/UserProfiles"));
const DeleteandDeactive = lazyWithRetry(() => import("./pages/DeleteandDeactive/DeleteandDeactive"));
import AppLayout from "./layout/AppLayout";
import { ScrollToTop } from "./components/common/ScrollToTop";
import Loader from "../src/components/common/Loader.tsx"


// Lazy-loaded: these were previously all imported eagerly at the top level,
// forcing the browser to parse/execute the entire app's JS bundle on first
// load regardless of which single page the user actually visits. Splitting
// them into separate chunks (loaded only when their route is hit) removes
// that unnecessary parse/execute cost without changing any route, prop, or
// rendered output.
const Videos = lazyWithRetry(() => import("./pages/UiElements/Videos"));
const Images = lazyWithRetry(() => import("./pages/UiElements/Images"));
const Badges = lazyWithRetry(() => import("./pages/UiElements/Badges"));
const Avatars = lazyWithRetry(() => import("./pages/UiElements/Avatars"));
const Buttons = lazyWithRetry(() => import("./pages/UiElements/Buttons"));
const LineChart = lazyWithRetry(() => import("./pages/Charts/LineChart"));
const BarChart = lazyWithRetry(() => import("./pages/Charts/BarChart"));
const Calendar = lazyWithRetry(() => import("./pages/Calendar"));
const BasicTables = lazyWithRetry(() => import("./pages/Tables/BasicTables"));
const FormElements = lazyWithRetry(() => import("./pages/Forms/FormElements"));
const Blank = lazyWithRetry(() => import("./pages/Blank"));
const Home = lazyWithRetry(() => import("./pages/Dashboard/Home"));
const Install = lazyWithRetry(() => import("./pages/Install"));
import { EnhancedAuthProvider, useAuth } from "./context/AuthContext";
import { EnhancedAuthProvider as PostLoginFlowProvider } from "./context/EnhancedAuthContext";
import { PdfDownloadProvider } from "./context/PdfDownloadContext";
import { ActiveStoreProvider } from "./context/ActiveStoreContext";
import { PermissionProvider } from "./context/PermissionContext";
import { WidgetProvider } from "./context/WidgetContext";
import { SightProvider } from "./context/SightContext";
import { AICardProvider } from "./context/AICardContext";
import { FeatureAccessProvider } from "./context/FeatureAccessContext";
import FeatureUpgradeModal from "./components/modals/FeatureUpgradeModal";
import axios from "axios";
import { GlobalMetaUpdater } from "./components/common/PageMeta";
import UrlParameterGuard from "./components/auth/UrlParameterGuard";
import AuthGuard from "./components/auth/AuthGuard";
// import SupportDashboard from "./pages/Support/SupportDashboard";
// import Invoices from "./pages/Invoices/Invoices";
// import CreditsHistory from "./pages/CreditsHistory/CreditsHistory";
// import IsightHandshake from "./pages/AuthPages/IsightHandshake";
// import Alert from "./pages/Alert/Alert";
// import WorkflowBuilder from "./pages/Workflow/WorkflowBuilder";
// import ISights from "./pages/ISights/ISights";
// import ISightV2Page from "./pages/isight-v2/ISightV2Page";
import { startSessionWatcher } from "./utils/sessionWatcher";
import Footer from "./components/footer/Footer";
import ProtectedRoute from "./components/routing/ProtectedRoute";
import "./utils/globalAppearanceListener";
import { ImpersonateLogin } from "./pages/ImpersonateLogin";
import IndexRedirect from "./pages/Dashboard/IndexRedirect";
import MemberOnlyRoute from "./components/auth/MemberOnlyRoute";
import { MasterSyncProvider } from "./context/MasterSyncContext";
import UnifiedGuard from "./components/routing/UnifiedGuard";
import SelectStoreRedirect from "./components/routing/SelectStoreRedirect";
import LoadingPage from "./pages/LoadingPage/LoadingPage";

const Products = lazyWithRetry(() => import("./pages/Ecommerce/Products"));
const Notification = lazyWithRetry(() => import("./pages/Notification/Notification"));
const SightPage = lazyWithRetry(() => import("./pages/Sights/SightPage"));
const GuestStoreDetails = lazyWithRetry(() => import("./pages/StoreHub/GuestStoreDetails"));
const KpiDemo = lazyWithRetry(() => import("./pages/Dashboard/KpiDemo"));
const TotalSalesDemo = lazyWithRetry(() => import("./pages/Dashboard/TotalSalesDemo"));
const ShopifyAppHome = lazyWithRetry(() => import("./pages/ShopifyApp/ShopifyAppHome"));
const ChoosePlan = lazyWithRetry(() => import("./pages/Billing/ChoosePlan"));
const ComparePlan = lazyWithRetry(() => import("./pages/Comparison/ComparePlan"));
const WooCommerceSuccess = lazyWithRetry(() => import("./pages/Billing/WooCommerceSuccess"));
const AccountSettings = lazyWithRetry(() => import("./pages/AccountSettings/AccountSettings"));
const BillingPage = lazyWithRetry(() => import("./pages/AccountSettings/BillingPage"));
const MetaAdsDashboard = lazyWithRetry(() => import("./pages/MetaAds/MetaAdsDashboard"));
const GoogleAdsDashboard = lazyWithRetry(() => import("./pages/GoogleAds/GoogleAdsDashboard"));
const GA4Dashboard = lazyWithRetry(() => import("./pages/GA4/GA4Dashboard"));
const GSCDashboard = lazyWithRetry(() => import("./pages/GSC/GSCDashboard"));
const Integrations = lazyWithRetry(() => import("./pages/Integrations/Integrations"));
const SupportDashboard = lazyWithRetry(() => import("./pages/Support/SupportDashboard"));
const Invoices = lazyWithRetry(() => import("./pages/Invoices/Invoices"));
const CreditsHistory = lazyWithRetry(() => import("./pages/CreditsHistory/CreditsHistory"));
const IsightHandshake = lazyWithRetry(() => import("./pages/AuthPages/IsightHandshake"));
const Alert = lazyWithRetry(() => import("./pages/Alert/Alert"));
const WorkflowBuilder = lazyWithRetry(() => import("./pages/Workflow/WorkflowBuilder"));
const ISights = lazyWithRetry(() => import("./pages/ISights/ISights"));
const EmailTemplate = lazyWithRetry(() => import("./pages/EmailTemplate/EmailTemplate"));
const AdminPanelApp = lazyWithRetry(() => import("./admin-panel/AdminPanelApp"));
const Intelligence = lazyWithRetry(() => import("./pages/intelligence/Intelligence"));
const ReportsPage = lazyWithRetry(() => import("./pages/Reports/ReportsPage"));
const ReportExport = lazyWithRetry(() => import("./pages/Reports/ReportExport"));
const MyReports = lazyWithRetry(() => import("./pages/Reports/MyReports"));
const LinkReport = lazyWithRetry(() => import("./pages/Reports/LinkReport.tsx"));
const ExecutiveSummaryPage = lazyWithRetry(() => import("./pages/Reports/ExecutiveSummaryPage"));
const Settings = lazyWithRetry(() => import("./pages/Settings/Settings"));
const ShopSetupPage = lazyWithRetry(() => import("./pages/shop-setup/ShopSetupPage"));
const ManualInputsPage = lazyWithRetry(() => import("./pages/Settings/ManualInputsPage"));
const CheckoutKpiDashboard = lazyWithRetry(() => import("./pages/CheckoutKpiDashboard"));
const NotificationSights = lazyWithRetry(() => import("./pages/Notification/NotificationSights"));
const NotificationLogTable = lazyWithRetry(() => import("./pages/Notification/NotificationLogTable"));
const HirePod = lazyWithRetry(() => import("./pages/pods/HirePod"));
const Connection = lazyWithRetry(() => import("./pages/Connection/Connection"));
const Connectionv1 = lazyWithRetry(() => import("./pages/Connection/Connectionv1"));
const InvitationLanding = lazyWithRetry(() => import("./pages/Invitation/InvitationLanding"));
const StoreHub = lazyWithRetry(() => import("./pages/StoreHub/StoreHub"));
const AgencyDashboard = lazyWithRetry(() => import("./pages/Agency/AgencyDashboard"));
const CreativeDetail = lazyWithRetry(() => import("./pages/CreativeDetail"));
const ReportRenderPage = lazyWithRetry(() => import("./pages/Reports/ReportRenderPage"));
const DynamicConnection = lazyWithRetry(() => import("./pages/DynamicConnection/DynamicConnection"));
const DynamicConnectionv1 = lazyWithRetry(() => import("./pages/DynamicConnection/DynamicConnectionv1"));
const Demolink = lazyWithRetry(() => import("./pages/Demo/Demolink"));
// import ReportsPage from "./pages/Reports/ReportsPage";


declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

function lazyWithRetry(factory: () => Promise<any>) {
  const key = "chunk-reload-attempted";
  const reloadOnce = (reason: string) => {
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      console.warn("[lazyWithRetry] chunk load failed, reloading once:", reason);
      window.location.reload();
      return new Promise(() => {}); // stall, page is reloading
    }
    return Promise.reject(new Error(reason));
  };
  return lazy(async () => {
    // A previous chunk failure caused a reload; load proved OK, so allow
    // future failures to trigger the one-time recovery again.
    sessionStorage.removeItem(key);
    try {
      const mod = await factory();
      // Stale/mismatched cached chunk can resolve to a module without the
      // expected default export -> React.lazy would throw
      // "Cannot read properties of undefined (reading 'default')".
      if (!mod || typeof mod.default === "undefined") {
        return reloadOnce(
          "Module resolved without a default export",
        ) as never;
      }
      return mod;
    } catch (err: any) {
      const msg = err?.message || String(err);
      if (
        /failed to fetch|dynamically imported module|importing a script|404|chunk|loading chunk|preload|script error|unexpected token|modulepreload/i.test(
          msg,
        )
      ) {
        return reloadOnce(msg) as never;
      }
      throw err;
    }
  });
}

window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  if (!sessionStorage.getItem("vite-chunk-reload")) {
    sessionStorage.setItem("vite-chunk-reload", "1");
    window.location.reload();
  }
});

// Reset recovery flags after a fresh successful load so a later chunk
// failure (stale hashed assets after a deploy) can still recover once.
window.addEventListener("load", () => {
  sessionStorage.removeItem("chunk-reload-attempted");
  sessionStorage.removeItem("vite-chunk-reload");
});

// Global Interface Settings Loader
function InterfaceSettingsLoader() {
  useEffect(() => {
    const loadInterfaceSettings = async () => {
      try {
        // Only load if user is authenticated
        if (localStorage.getItem("token")) {
          const response = await axios.get("/user/interface-settings");
          const settings = response.data.settings || {};

          // Apply compact mode globally
          if (settings.compact_mode) {
            document.body.classList.add("compact-mode");
          } else {
            document.body.classList.remove("compact-mode");
          }

          // Apply other interface settings as needed
          if (settings.animations_enabled === false) {
            document.body.classList.add("no-animations");
          } else {
            document.body.classList.remove("no-animations");
          }
        }
      } catch (error) {
        console.error("Failed to load interface settings:", error);
      }
    };

    loadInterfaceSettings();
  }, []);

  return null;
}

// Global Appearance Settings Loader
function AppearanceSettingsLoader() {
  useEffect(() => {
    const loadAppearanceSettings = async () => {
      try {
        // Only load if user is authenticated
        if (localStorage.getItem("token")) {
          const response = await axios.get("/user/appearance-settings");
          const settings = response.data.settings || {};

          // Document theme is owned only by ThemeContext (theme-shopify). Do not apply
          // settings.theme here - it arrived after paint and caused dark → light flash.

          // Store other settings in localStorage for global access
          localStorage.setItem("user_timezone", settings.timezone || "UTC");
          localStorage.setItem(
            "user_date_format",
            settings.date_format || "MM/DD/YYYY",
          );
          localStorage.setItem(
            "user_time_format",
            settings.time_format || "12h",
          );
        }
      } catch (error) {
        console.error("Failed to load appearance settings:", error);
      }
    };

    loadAppearanceSettings();
  }, []);

  return null;
}

// Component to redirect backend routes to Laravel
// This prevents React Router from intercepting Laravel routes like /apps/shopify/callback
function BackendRouteRedirect() {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    const search = location.search;

    // Log route navigation
    logger.route(pathname, search, location.state);

    // Store previous path for logging
    const previousPath = sessionStorage.getItem("current_path");
    if (previousPath && previousPath !== pathname + search) {
      sessionStorage.setItem("previous_path", previousPath);
    }
    sessionStorage.setItem("current_path", pathname + search);

    // Backend routes that should be handled by Laravel, not React Router
    // /apps/ - Shopify proxy routes (e.g., /apps/shopify/callback, /apps//shopify/callback)
    // /store/.../apps/ - Shopify admin URL patterns (e.g., /store/shopname/apps//billing/choose-plan, /store/shopname/apps//shopify/billing)
    // /shopify/ - Direct Shopify routes (e.g., /shopify/callback)
    // /webhook - Webhook endpoints
    // Note: /billing/choose-plan is a React route, so we don't intercept it
    const backendRoutePatterns = [
      /^\/apps\//, // All /apps/* routes (including double-slash /apps//shopify/callback)
      /\/store\/[^\/]+\/apps/, // Shopify admin URL patterns with /store/shopname/apps (handles both /apps/ and /apps//)
      /^\/shopify\//, // All /shopify/* routes
      /^\/webhook/, // Webhook routes
    ];

    // Check if current path is a backend route
    const isBackendRoute = backendRoutePatterns.some((pattern) =>
      pattern.test(pathname),
    );

    if (isBackendRoute) {
      // Get the backend URL from environment variable
      // VITE_API_BASE_URL should be the full backend API URL (e.g., https://api.netsight.ai)
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";
      const currentHost = window.location.hostname;
      console.log(apiBaseUrl);
      // Check if we're already on the backend API domain - if so, don't redirect
      let isOnBackendDomain = false;

      if (
        apiBaseUrl &&
        (apiBaseUrl.startsWith("http://") || apiBaseUrl.startsWith("https://"))
      ) {
        try {
          const apiUrl = new URL(apiBaseUrl.replace(/\/api\/?$/, ""));
          isOnBackendDomain = currentHost === apiUrl.hostname;
        } catch (e) {
          // URL parsing failed, check if hostname matches pattern
          if (currentHost.startsWith("api.")) {
            isOnBackendDomain = true;
          }
        }
      } else if (currentHost.startsWith("api.")) {
        isOnBackendDomain = true;
      }

      // If we're already on the backend domain, don't redirect (nginx should handle it)
      if (isOnBackendDomain) {
        console.warn(
          "Backend route detected on backend domain - nginx should handle this:",
          {
            pathname,
            hostname: currentHost,
          },
        );
        return;
      }

      // Check if we've already tried to redirect (prevent infinite loops)
      const redirectKey = `backend_redirect_${pathname}`;
      const hasRedirected = sessionStorage.getItem(redirectKey);

      if (hasRedirected) {
        // Already redirected once, don't redirect again
        // This means nginx isn't proxying correctly or Laravel isn't responding
        console.error(
          "Backend route redirect loop detected. Nginx may not be configured correctly.",
          {
            pathname,
            search,
            hostname: currentHost,
          },
        );
        return;
      }

      // Mark that we're redirecting
      sessionStorage.setItem(redirectKey, "true");

      let backendUrl: string;

      if (
        apiBaseUrl &&
        (apiBaseUrl.startsWith("http://") || apiBaseUrl.startsWith("https://"))
      ) {
        // Full URL provided
        backendUrl = apiBaseUrl.replace(/\/api\/?$/, ""); // Remove /api suffix if present
      } else if (apiBaseUrl && apiBaseUrl.startsWith("/")) {
        // Relative path - use current origin
        backendUrl = window.location.origin;
      } else {
        // Fallback: try to derive from current origin (assuming api subdomain pattern)
        // If we're on app.netsight.ai, redirect to api.netsight.ai
        const currentHost = window.location.hostname;
        if (currentHost.startsWith("app.")) {
          backendUrl =
            window.location.protocol +
            "//" +
            currentHost.replace("app.", "api.");
        } else {
          // Last resort: use current origin
          backendUrl = window.location.origin;
        }
      }

      const fullUrl = backendUrl + pathname + search;

      console.log("Backend route detected, redirecting to Laravel:", {
        pathname,
        backendUrl,
        fullUrl,
        apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
        reason: "React Router should not handle backend routes",
      });

      // Redirect to backend (full page reload to bypass React Router)
      // This will hit nginx, which should proxy to Laravel
      // If nginx is configured correctly, Laravel will handle the route
      window.location.replace(fullUrl);
    } else {
      // Not a backend route, clear any redirect flags
      // This allows normal navigation
      const redirectKey = `backend_redirect_${pathname}`;
      sessionStorage.removeItem(redirectKey);
    }
  }, [location]);

  return null;
}

/** Paths where the main shell (AppLayout) already renders <Footer /> - avoid duplicate. */
function isAppShellDashboardPath(pathname: string): boolean {
  if (pathname === "/") return false;
  if (
    /^\/[^/]+\/sight\/[^/]+$/.test(pathname) ||
    /^\/sight\/[^/]+$/.test(pathname)
  ) {
    return true;
  }
  const m = pathname.match(/^\/([^/]+)$/);
  if (!m) return false;
  const seg = m[1];
  const known = new Set([
    "legacy",
    "kpi-demo",
    "total-sales-demo",
    "profile",
    "settings",
    "manual-inputs",
    "billing",
    "invoices",
    "credits-history",
    "integrations",
    "connection",
    "neo-intelligence",
    "support",
    "meta-ads",
    "google-ads",
    "ga4",
    "gsc",
    "calendar",
    "blank",
    "isights",
    // "notification",
    "form-elements",
    "basic-tables",
    "alerts",
    "avatars",
    "badge",
    "buttons",
    "images",
    "videos",
    "line-chart",
    "bar-chart",
    "products",
  ]);
  return known.has(seg);
}

function AppFooter() {
  const location = useLocation();

  const path = location.pathname;

  if (
    path.startsWith("/") ||
    path.startsWith("/admin") ||
    path.startsWith("/signin") ||
    path.startsWith("/signup") ||
    path.startsWith("/forgot-password") ||
    path.startsWith("/notification") ||
    path.startsWith("/neo-intelligence") ||
    path.startsWith("/d0cda6f0439c")
  ) {
    return null;
  }

  if (isAppShellDashboardPath(path)) {
    return null;
  }

  return <Footer />;
}

// Push notification initializer - must be inside a React component
// function PushNotificationInit() {
//   const { currentShop } = useAuth();

//   useEffect(() => {
//     if (!currentShop) return;

//     // Enable push notifications for the currently active shop.
//     // Browsers may block the permission prompt if not triggered by a user gesture.
//     // We still attempt it here (works in Chrome); for other browsers the permission
//     // must be granted via browser settings or a user-initiated action.
//     // enablePushNotifications(currentShop).then((ok) => {
//     //   if (!ok) {
//     //     console.warn('[Push] Push notifications not enabled - permission may be denied or browser unsupported.');
//     //   }
//     // });

//     // Foreground message listener
//     const unsubscribe = onForegroundMessage((title, body) => {
//       // Show notification via service worker (works even when tab is focused)
//       if ('serviceWorker' in navigator) {
//         navigator.serviceWorker.ready.then((registration) => {
//           registration.showNotification(title, {
//             body,
//             icon: '/logo.svg',
//             badge: '/logo.svg',
//             tag: 'netsight-push',
//           });
//         }).catch(() => {
//           // Fallback: log to console if service worker not ready
//           console.info(`[Push] ${title}: ${body}`);
//         });
//       }
//     });

//     return () => unsubscribe();
//   }, [currentShop]);

//   return null;
// }

// Global cleaner to remove ?shop=... from browser URL on any route change,
// using history.replaceState to avoid triggering React Router navigation loops.
function ShopParamCleaner() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("shop")) {
        url.searchParams.delete("shop");
        window.history.replaceState({}, "", url.toString());
      }
    } catch {
      // ignore URL parsing issues
    }
  }, [location.pathname, location.search]);

  return null;
}

axios.defaults.baseURL = import.meta.env.VITE_API_BASE_URL;

axios.defaults.withCredentials = true;
axios.defaults.headers.common["Content-Type"] = "application/json";
axios.defaults.headers.common["Accept"] = "application/json";

// Ensure bare fetch calls to API endpoints include Authorization header.
// Handles both relative (/api/*) and absolute (http://host/api/*) URLs.
declare global {
  interface Window {
    __netsightOriginalFetch?: typeof window.fetch;
    __netsightFetchPatched?: boolean;
  }
}

if (!window.__netsightFetchPatched) {
  window.__netsightOriginalFetch =
    window.__netsightOriginalFetch || window.fetch.bind(window);
  const originalFetch = window.__netsightOriginalFetch;

  const isApiRequest = (rawUrl: string): boolean => {
    if (rawUrl.startsWith("/api/")) return true;

    try {
      const parsed = new URL(rawUrl, window.location.origin);
      return parsed.pathname.startsWith("/api/");
    } catch {
      return false;
    }
  };

  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    try {
      const rawUrl =
        typeof input === "string"
          ? input
          : input instanceof Request
            ? input.url
            : input.toString();

      if (isApiRequest(rawUrl)) {
        const token = (
          localStorage.getItem("token") ||
          sessionStorage.getItem("token") ||
          ""
        ).trim();
        if (token) {
          const headers = new Headers(
            init?.headers ||
              (input instanceof Request ? input.headers : undefined),
          );
          if (!headers.has("Authorization")) {
            headers.set("Authorization", `Bearer ${token}`);
          }
          return originalFetch(input, { ...init, headers });
        }
      }
    } catch {
      // no-op
    }
    return originalFetch(input, init);
  };

  window.__netsightFetchPatched = true;
}

function SessionWatcher() {
  const { logout, sessionExpiresAt } = useAuth() as any;

  useEffect(() => {
    const stop = startSessionWatcher({
      getExpiry: () => sessionExpiresAt ?? null,
      onExpire: () => {
        // Graceful logout; axios will also handle 401 redirects if needed
        if (typeof logout === "function") {
          logout();
        } else {
          window.location.reload();
        }
      },
    });
    return stop;
  }, [logout, sessionExpiresAt]);

  return null;
}

// Global Invite Handler - Auto-accept invitations from URL parameters
function GlobalInviteHandler() {
  const location = useLocation();

  useEffect(() => {
    // Check for invite parameter in URL
    const searchParams = new URLSearchParams(location.search);
    const inviteToken = searchParams.get("invite");

    if (inviteToken && typeof window !== "undefined") {
      console.log(
        "GlobalInviteHandler: Found invite token in URL:",
        inviteToken,
      );

      // Check if we've already processed this invite
      const processedKey = `invite_processed_${inviteToken}`;
      const alreadyProcessed = localStorage.getItem(processedKey);

      if (!alreadyProcessed) {
        // Store the invite token for processing after login
        localStorage.setItem("invitation_token", inviteToken);

        // Mark as processed to prevent duplicate processing
        localStorage.setItem(processedKey, "true");

        console.log(
          "GlobalInviteHandler: Stored invite token for processing after login",
        );

        // If user is already logged in, store token for modal display (don't auto-accept)
        if (localStorage.getItem("token")) {
          console.log(
            "GlobalInviteHandler: User logged in, storing token for modal display",
          );
          // Don't auto-accept - let the user decide via the invitation modal
        }
      } else {
        console.log("GlobalInviteHandler: Invite already processed, skipping");
      }
    }
  }, [location.search]);

  return null; // Return null as this is a utility component
}

function MetaPixelPageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    if (window.fbq) {
      window.fbq("track", "PageView");
    }
  }, [location.pathname]);

  return null;
}
// Process invite acceptance
async function processInviteAcceptance(token: string) {
  try {
    console.log(
      "GlobalInviteHandler: Processing invite acceptance for token:",
      token,
    );

    // Call accept-invite API
    const response = await axios.post("/invitations/accept", { token });

    if (response.data.success) {
      console.log("GlobalInviteHandler: Invite accepted successfully");

      // Show success notification
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("invitation_accepted", {
            detail: {
              accepted: true,
              storeName: response.data.store?.name || "Unknown Store",
              message:
                response.data.message || "Invitation accepted successfully",
            },
          }),
        );
      }

      // Clear invite token and redirect to profile
      localStorage.removeItem("invitation_token");

      // Set global flag to prevent duplicate processing in StoreHub
      localStorage.setItem("invite_processed_globally", "true");

      // Clear URL parameters
      const url = new URL(window.location.href);
      url.searchParams.delete("invite");
      window.history.replaceState({}, "", url.toString());

      // Redirect to profile with refresh to clear banners
      window.location.href = "/profile?refresh=" + Date.now();
    } else {
      console.error(
        "GlobalInviteHandler: Invite acceptance failed:",
        response.data,
      );

      // Show error notification
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("invitation_error", {
            detail: {
              message: response.data.message || "Failed to accept invitation",
            },
          }),
        );
      }
    }
  } catch (error) {
    console.error("GlobalInviteHandler: Error processing invite:", error);

    // Show error notification
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("invitation_error", {
          detail: {
            message: "Error accepting invitation",
          },
        }),
      );
    }
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <PdfDownloadProvider>
      <Router>
        <EnhancedAuthProvider>
          <PostLoginFlowProvider>
            <PermissionProvider>
              <ActiveStoreProvider>
                <WidgetProvider>
                  <AICardProvider>
                    <SightProvider>
                      <FeatureAccessProvider>
                        <GlobalMetaUpdater />
                        <BackendRouteRedirect />
                        <ShopParamCleaner />
                        <GlobalInviteHandler />
                        <ScrollToTop />
                        <SessionWatcher />
                        {/* <PushNotificationInit /> */}
                        <InterfaceSettingsLoader />
                        <AppearanceSettingsLoader />
                        <MetaPixelPageViewTracker />
                        <AnalyticsProvider />
                        <FeatureUpgradeModal />
                        <UnifiedGuard>
                          <Suspense fallback={<></>}>
                          <Routes>
                            {/* Root checks for token: if present redirect to /app (which handles default sight), else /signin */}
                            <Route path="/loading-page" element={<LoadingPage />} />

                            {/* Admin panel (merged from netsight-admin-frontend) */}
                            <Route
                              path="/admin/*"
                              element={<AdminPanelApp />}
                            />
                            <Route
                              path="/impersonate-login"
                              element={<ImpersonateLogin />}
                            />

                            {/* Member-only Store Hub - Outside main layout to hide header/sidebar */}
                            <Route
                              path="/stores"
                              element={
                                <UrlParameterGuard>
                                  <EnhancedAuthProvider>
                                    <PostLoginFlowProvider>
                                      <PermissionProvider>
                                        <ActiveStoreProvider>
                                          <MemberOnlyRoute>
                                            <StoreHub />
                                          </MemberOnlyRoute>
                                        </ActiveStoreProvider>
                                      </PermissionProvider>
                                    </PostLoginFlowProvider>
                                  </EnhancedAuthProvider>
                                </UrlParameterGuard>
                              }
                            />

                            {/* Guest Store Details - Outside main layout */}
                            <Route
                              path="/guest-store/:storeId"
                              element={<GuestStoreDetails />}
                            />

                            {/* Dashboard Layout moved under /app */}
                            <Route
                              path="/"
                              element={
                                <UrlParameterGuard>
                                  <AppLayout />
                                </UrlParameterGuard>
                              }
                            >
                              {/* Index: role-based redirect to appropriate dashboard */}
                              <Route index element={<IndexRedirect />} />
                              <Route path="legacy" element={<Home />} />
                              <Route path="kpi-demo" element={<KpiDemo />} />
                              <Route
                                path="total-sales-demo"
                                element={<TotalSalesDemo />}
                              />
                              {/* Legacy slug-based route: redirect to hashed route when possible (handled in SightPage) */}
                              <Route
                                path="sight/:slug"
                                element={<SightPage />}
                              />
                              {/* New tenantKey + hashedId route */}
                              <Route
                                path=":tenantKey/sight/:hashedId"
                                element={<SightPage />}
                              />

                              {/* Others Page */}
                              <Route
                                path="profile"
                                element={<UserProfiles />}
                              />
                              <Route path="Demolink" element={<Demolink />} />

                              <Route
                                path="profile/manage-account"
                                element={<DeleteandDeactive />}
                              />
                              <Route
                                path="settings"
                                element={
                                  <ProtectedRoute
                                    permission="settings_access"
                                    redirectTo="/profile"
                                  >
                                    <Settings />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="manual-inputs"
                                element={
                                  <ProtectedRoute
                                    permission="sight_access"
                                    redirectTo="/profile"
                                  >
                                    <ManualInputsPage />
                                  </ProtectedRoute>
                                }
                              />

                              <Route
                                path="billing"
                                element={
                                  <ProtectedRoute
                                    permission="billing_access"
                                    redirectTo="/profile"
                                  >
                                    <BillingPage />
                                  </ProtectedRoute>
                                }
                              />

                              {/* <Route
                                path="billingv2"
                                element={
                                  <ProtectedRoute
                                    permission="billing_access"
                                    redirectTo="/profile"
                                  >
                                    <BillingPage />
                                  </ProtectedRoute>
                                }
                              /> */}
                              <Route
                                path="invoices"
                                element={
                                  <ProtectedRoute
                                    permission="billing_access"
                                    redirectTo="/profile"
                                  >
                                    <Invoices />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="credits-history"
                                element={
                                  <ProtectedRoute
                                    permission="billing_access"
                                    redirectTo="/profile"
                                  >
                                    <CreditsHistory />
                                  </ProtectedRoute>
                                }
                              />
                              <Route
                                path="integrations"
                                element={
                                  <ProtectedRoute
                                    permission="integrations_access"
                                    redirectTo="/profile"
                                  >
                                    <DynamicConnection />
                                  </ProtectedRoute>
                                }
                              />
                              {/* <Route path="dynamic-connection" element={<ProtectedRoute><DynamicConnection /></ProtectedRoute>} />
                            <Route path="dynamic-connectionv1" element={<ProtectedRoute><DynamicConnectionv1 /></ProtectedRoute>} /> */}
                              <Route
                                path="shop-setup"
                                element={<ShopSetupPage />}
                              />
                              {/* <Route path="memberview" element={<AgencyDashboard />} /> */}
                              {/* <Route path="neo-intelligence" element={<Intelligence />} /> */}
                              <Route
                                path="neo-intelligence/marketing"
                                element={
                                  <Intelligence focusedSection="marketing" />
                                }
                              />
                              <Route
                                path="neo-intelligence/ads-intelligence"
                                element={
                                  <Intelligence
                                    focusedSection="marketing"
                                    intelTab="ads"
                                  />
                                }
                              />
                              <Route
                                path="neo-intelligence/spend-intelligence"
                                element={
                                  <Intelligence
                                    focusedSection="marketing"
                                    intelTab="spend"
                                  />
                                }
                              />
                              <Route
                                path="neo-intelligence/organic-intelligence"
                                element={
                                  <Intelligence
                                    focusedSection="marketing"
                                    intelTab="seo"
                                  />
                                }
                              />
                              <Route
                                path="neo-intelligence/product"
                                element={
                                  <Intelligence focusedSection="product" />
                                }
                              />
                              <Route
                                path="neo-intelligence/customer"
                                element={
                                  <Intelligence focusedSection="customer" />
                                }
                              />
                              <Route
                                path="neo-intelligence/store"
                                element={
                                  <Intelligence focusedSection="store" />
                                }
                              />
                              <Route
                                path="neo-intelligence/isight-ai"
                                element={<Intelligence focusedSection="ai" />}
                              />
                              <Route
                                path="neo-intelligence/payment"
                                element={
                                  <Intelligence focusedSection="payment" />
                                }
                              />
                              <Route
                                path="neo-intelligence/logistics"
                                element={
                                  <Intelligence focusedSection="logistics" />
                                }
                              />
                              <Route
                                path="neo-intelligence/infrastructure"
                                element={
                                  <Intelligence focusedSection="infrastructure" />
                                }
                              />
                              <Route
                                path=":tenantKey/neo-intelligence/infrastructure"
                                element={
                                  <Intelligence focusedSection="infrastructure" />
                                }
                              />
                              <Route
                                path="neo-intelligence"
                                element={<Intelligence />}
                              />
                              <Route
                                path=":tenantKey/neo-intelligence"
                                element={<Intelligence />}
                              />
                              <Route
                                path=":tenantKey/neo-intelligence/marketing"
                                element={
                                  <Intelligence focusedSection="marketing" />
                                }
                              />
                              <Route
                                path=":tenantKey/neo-intelligence/ads-intelligence"
                                element={
                                  <Intelligence
                                    focusedSection="marketing"
                                    intelTab="ads"
                                  />
                                }
                              />
                              <Route
                                path=":tenantKey/neo-intelligence/spend-intelligence"
                                element={
                                  <Intelligence
                                    focusedSection="marketing"
                                    intelTab="spend"
                                  />
                                }
                              />
                              <Route
                                path=":tenantKey/neo-intelligence/organic-intelligence"
                                element={
                                  <Intelligence
                                    focusedSection="marketing"
                                    intelTab="seo"
                                  />
                                }
                              />
                              <Route
                                path=":tenantKey/neo-intelligence/product"
                                element={
                                  <Intelligence focusedSection="product" />
                                }
                              />
                              <Route
                                path=":tenantKey/neo-intelligence/customer"
                                element={
                                  <Intelligence focusedSection="customer" />
                                }
                              />
                              <Route
                                path=":tenantKey/neo-intelligence/store"
                                element={
                                  <Intelligence focusedSection="store" />
                                }
                              />
                              <Route
                                path=":tenantKey/neo-intelligence/isight-ai"
                                element={<Intelligence focusedSection="ai" />}
                              />
                              <Route
                                path=":tenantKey/neo-intelligence/payment"
                                element={
                                  <Intelligence focusedSection="payment" />
                                }
                              />
                              <Route
                                path=":tenantKey/neo-intelligence/logistics"
                                element={
                                  <Intelligence focusedSection="logistics" />
                                }
                              />

                              <Route
                                path="neo-intelligence/checkout"
                                element={
                                  <Intelligence focusedSection="checkout" />
                                }
                              />
                              <Route
                                path=":tenantKey/neo-intelligence/checkout"
                                element={
                                  <Intelligence focusedSection="checkout" />
                                }
                              />

                              <Route
                                path="neo-intelligence/creative/:adId"
                                element={<CreativeDetail />}
                              />
                              <Route
                                path=":tenantKey/neo-intelligence/creative/:adId"
                                element={<CreativeDetail />}
                              />

                              <Route path="ceo-summary" element={<ReportsPage />} />
                              <Route
                                path="custom-reports"
                                element={<ReportExport />}
                              />
                              <Route
                                path="my-reports"
                                element={<MyReports />}
                              />
                              <Route
                                path="reports"
                                element={<LinkReport />}
                              />
                              <Route
                                path=":tenantKey/reports"
                                element={<ReportsPage />}
                              />
                              <Route
                                path="executive-summary"
                                element={<ExecutiveSummaryPage />}
                              />
                              <Route
                                path=":tenantKey/executive-summary"
                                element={<ExecutiveSummaryPage />}
                              />
                              <Route
                                path="support"
                                element={<SupportDashboard />}
                              />
                              <Route
                                path="meta-ads"
                                element={<MetaAdsDashboard />}
                              />
                              <Route
                                path="google-ads"
                                element={<GoogleAdsDashboard />}
                              />
                              <Route path="ga4" element={<GA4Dashboard />} />
                              <Route path="gsc" element={<GSCDashboard />} />
                              <Route path="calendar" element={<Calendar />} />
                              <Route path="blank" element={<Blank />} />
                              <Route
                                path="sights"
                                element={<Navigate to="/isights" replace />}
                              />
                              <Route
                                path="isights"
                                element={
                                  <ProtectedRoute
                                    permission="isight_access"
                                    redirectTo="/profile"
                                  >
                                    <ISights />
                                  </ProtectedRoute>
                                }
                              />

                              {/* <Route
                                path="isight-v2"
                                element={
                                  <ProtectedRoute
                                    permission="isight_access"
                                    redirectTo="/profile"
                                  >
                                    <ISightV2Page />
                                  </ProtectedRoute>
                                }
                              /> */}

                              {/* <Route path="notification-center" element={<ProtectedRoute permission="notification_access" redirectTo="/profile"><NotificationSights /></ProtectedRoute>} /> */}
                              {/* Forms */}
                              <Route
                                path="form-elements"
                                element={<FormElements />}
                              />
                            </Route>

                            {/* Billing routes */}
                            <Route
                              path="/billing/choose-plan"
                              element={
                                <UrlParameterGuard>
                                  <ChoosePlan />
                                </UrlParameterGuard>
                              }
                            />

                            {/* Embedded Shopify app entry route.
                                Shopify opens the app at the root URL, UnifiedGuard
                                forwards it here; ShopifyAppHome performs the
                                embedded-auth token exchange and auto-login. */}
                            <Route
                              path="/shopify-app"
                              element={
                                <UrlParameterGuard>
                                  <ShopifyAppHome />
                                </UrlParameterGuard>
                              }
                            />

                            {/* Compare plan routes */}
                            <Route
                              path="/compare-plans"
                              element={
                                <UrlParameterGuard>
                                  <ComparePlan />
                                </UrlParameterGuard>
                              }
                            />
                          {/* alert page S */}
                          <Route path="/alert" element={
                            <UrlParameterGuard>
                              <Alert />
                            </UrlParameterGuard>
                          } />
                           {/* Workflow Builder page */}
                           
                          {/* Auth Layout (keep available) */}
                          <Route path="/signin" element={<SignIn />} />
                          <Route path="/signup" element={<SignUp />} />
                          <Route path="/forgot-password" element={<ForgotPassword />} />
                          <Route path="/password/reset/:token" element={<ResetPasswordRedirect />} />
                          <Route path="/reset-password" element={<ResetPassword />} />
                          <Route path="/setup-account" element={<SetupAccount />} />
                          <Route path="/auth/isight-handshake" element={<IsightHandshake />} />
                          <Route path="/account-reactivate" element={<AccountReactivate />} />
                          <Route path="/email-template" element={
                            <UrlParameterGuard>
                              <EmailTemplate />
                            </UrlParameterGuard>
                          } />

                            <Route
                              path="/billing/woocommerce/success"
                              element={<WooCommerceSuccess />}
                            />
                            <Route
                              path="/memberview"
                              element={
                                <UrlParameterGuard>
                                  <AgencyDashboard />
                                </UrlParameterGuard>
                              }
                            />
                            <Route
                              path="/notification"
                              element={
                                <UrlParameterGuard>
                                  <ProtectedRoute
                                    permission="notification_access"
                                    redirectTo="/profile"
                                  >
                                    <NotificationSights />
                                  </ProtectedRoute>
                                </UrlParameterGuard>
                              }
                            />
                             <Route
                              path="/workflow"
                              element={
                                <UrlParameterGuard>
                                  <ProtectedRoute
                                    permission="notification_access"
                                    redirectTo="/settings"
                                  >
                                   <WorkflowBuilder />
                                  </ProtectedRoute>
                                </UrlParameterGuard>
                              }
                            />
                            <Route
                              path="/notification-log"
                              element={
                                <UrlParameterGuard>
                                  <ProtectedRoute
                                    permission="notification_access"
                                    redirectTo="/profile"
                                  >
                                    <NotificationLogTable />
                                  </ProtectedRoute>
                                </UrlParameterGuard>
                              }
                            />
                            <Route
                              path="/hire-pod"
                              element={
                                <UrlParameterGuard>
                                  <ProtectedRoute
                                    permission="notification_access"
                                    redirectTo="/profile"
                                  >
                                    <HirePod />
                                  </ProtectedRoute>
                                </UrlParameterGuard>
                              }
                            />
                            <Route
                              path="/Connection"
                              element={
                                <UrlParameterGuard>
                                  <ProtectedRoute>
                                    <DynamicConnection />
                                  </ProtectedRoute>
                                </UrlParameterGuard>
                              }
                            />
                            <Route
                              path="/Connectionv1"
                              element={
                                <UrlParameterGuard>
                                  <ProtectedRoute>
                                    <DynamicConnectionv1 />
                                  </ProtectedRoute>
                                </UrlParameterGuard>
                              }
                            />

                            {/* <Route path="/connection" element={<ProtectedRoute><Connection /></ProtectedRoute>} /> */}
<Route
                                path="/on-boarding"
                                element={
                                  <AuthGuard>
                                    <ProtectedRoute>
                                      <MasterSyncProvider>
                                        {/* <Footer > */}
                                        <DynamicConnectionv1 />
                                        <Footer />
                                      </MasterSyncProvider>
                                    </ProtectedRoute>
                                  </AuthGuard>
                                }
                              />
                              <Route
                                path="/checkout-kpi"
                                element={
                                  <UrlParameterGuard>
                                    <ProtectedRoute permission="sight_access" redirectTo="/profile">
                                      <CheckoutKpiDashboard />
                                    </ProtectedRoute>
                                  </UrlParameterGuard>
                                }
                              />

                            {/* alert page S */}
                            <Route
                              path="/alert"
                              element={
                                <UrlParameterGuard>
                                  <Alert />
                                </UrlParameterGuard>
                              }
                            />
                            {/* Auth Layout (keep available) */}
                            <Route path="/signin" element={<SignIn />} />
                            <Route path="/signup" element={<SignUp />} />
                            <Route
                              path="/forgot-password"
                              element={<ForgotPassword />}
                            />
                            <Route
                              path="/password/reset/:token"
                              element={<ResetPasswordRedirect />}
                            />
                            <Route
                              path="/reset-password"
                              element={<ResetPassword />}
                            />
                            <Route
                              path="/setup-account"
                              element={<SetupAccount />}
                            />
                            <Route
                              path="/auth/isight-handshake"
                              element={<IsightHandshake />}
                            />
                            <Route
                              path="/account-reactivate"
                              element={<AccountReactivate />}
                            />
                            <Route
                              path="/email-template"
                              element={
                                <UrlParameterGuard>
                                  <EmailTemplate />
                                </UrlParameterGuard>
                              }
                            />

                            {/* Invitation routes (Store Hub /stores is defined above with MemberOnlyRoute) */}
                            <Route
                              path="/invite/:token"
                              element={
                                <UrlParameterGuard>
                                  <InvitationLanding />
                                </UrlParameterGuard>
                              }
                            />
                            <Route
                              path="/select-store"
                              element={
                                <UrlParameterGuard>
                                  <SelectStoreRedirect />
                                </UrlParameterGuard>
                              }
                            />
                            <Route
                              path="/dashboard"
                              element={<Navigate to="/neo-intelligence" replace />}
                            />
                            <Route
                              path="/dashboard/*"
                              element={<Navigate to="/neo-intelligence" replace />}
                            />
                            <Route
                              path="/dashboard-:legacyHash"
                              element={<Navigate to="/neo-intelligence" replace />}
                            />

                            {/* Public render route - used by headless Chrome PDF job only */}
                            <Route path="/my-reports/render/:id" element={<Suspense fallback={<div style={{padding:40,color:"#666"}}>Loading…</div>}><ReportRenderPage /></Suspense>} />

                            {/* Fallback Route */}
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                          </Suspense>
                        </UnifiedGuard>
                        <AppFooter />
                      </FeatureAccessProvider>
                    </SightProvider>
                  </AICardProvider>
                </WidgetProvider>
              </ActiveStoreProvider>
            </PermissionProvider>
          </PostLoginFlowProvider>
        </EnhancedAuthProvider>
        <AdminToastHost />
      </Router>
      </PdfDownloadProvider>
    </ErrorBoundary>
  );
}
